import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { editCreation, duckTitle, patchGalleryCache, MAX_NAME, MAX_STORY } from "../lib/api";

// Inline form for the duck story (name + description). Saves through PATCH /creations/{id}; the parent swaps it in.
// The name here IS the duck's name: saving it renames the duck too.
export const StoryEditor = ({ creation, onSaved, onCancel }) => {
  const story = creation.story || {};
  const [name, setName] = useState(duckTitle(creation));
  const [description, setDescription] = useState(story.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!name.trim()) return setError("Give your duck a name");
    setBusy(true);
    setError("");
    try {
      const { data } = await editCreation(creation.id, { story_name: name, story_description: description });
      patchGalleryCache(creation.id, { name: data.name });
      onSaved(data);
      toast.success("Story saved");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save the story");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="story-editor" className="space-y-3" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <div>
        <input
          data-testid="story-name-input"
          value={name}
          maxLength={MAX_NAME}
          onChange={(e) => setName(e.target.value)}
          placeholder="Duck name"
          className="w-full rounded-xl border border-border bg-background px-3 py-2 font-heading text-lg font-bold focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <p className="mt-1 text-right text-[11px] font-mono2 text-muted-foreground" data-testid="story-name-count">{name.length}/{MAX_NAME}</p>
      </div>
      <div>
        <textarea
          data-testid="story-description-input"
          value={description}
          maxLength={MAX_STORY}
          rows={5}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Tell this duck's story (plain text — links are removed)"
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <p className="mt-1 text-right text-[11px] font-mono2 text-muted-foreground" data-testid="story-description-count">{description.length}/{MAX_STORY}</p>
      </div>
      {error && <p data-testid="story-editor-error" className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" className="rounded-full" data-testid="story-cancel-btn" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button type="submit" size="sm" className="rounded-full font-bold gap-1.5" data-testid="story-save-btn" disabled={busy}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
        </Button>
      </div>
    </form>
  );
};
